import {
  Clock,
  Upload,
  Eye,
  AlertTriangle,
  CheckCircle,
  Rocket,
  XCircle,
  Pause,
  RotateCcw,
} from "lucide-react";

// Creative submission status configuration
export const SUBMISSION_STATUSES = {
  pending: {
    label: "Pending",
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/20",
    borderColor: "border-yellow-500/30",
    icon: Clock,
    description: "Waiting for review",
    actionable: true,
    canTransitionTo: ["uploading", "in_review", "rejected"],
  },
  uploading: {
    label: "Uploading",
    color: "text-blue-400",
    bgColor: "bg-blue-500/20",
    borderColor: "border-blue-500/30",
    icon: Upload,
    description: "Currently uploading assets",
    actionable: false,
    canTransitionTo: ["pending", "in_review"],
  },
  in_review: {
    label: "In Review",
    color: "text-purple-400",
    bgColor: "bg-purple-500/20",
    borderColor: "border-purple-500/30",
    icon: Eye,
    description: "Under review by media buyer",
    actionable: true,
    canTransitionTo: ["changes_needed", "approved", "rejected", "on_hold"],
  },
  changes_needed: {
    label: "Changes Needed",
    color: "text-orange-400",
    bgColor: "bg-orange-500/20",
    borderColor: "border-orange-500/30",
    icon: AlertTriangle,
    description: "Requires modifications",
    actionable: true,
    canTransitionTo: ["revision_pending", "in_review", "rejected"],
  },
  approved: {
    label: "Approved",
    color: "text-green-400",
    bgColor: "bg-green-500/20",
    borderColor: "border-green-500/30",
    icon: CheckCircle,
    description: "Approved and ready to launch",
    actionable: true,
    canTransitionTo: ["launched", "on_hold", "changes_needed"],
  },
  launched: {
    label: "Launched",
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/20",
    borderColor: "border-emerald-500/30",
    icon: Rocket,
    description: "Successfully launched",
    actionable: false,
    canTransitionTo: ["on_hold"],
  },
  rejected: {
    label: "Rejected",
    color: "text-red-400",
    bgColor: "bg-red-500/20",
    borderColor: "border-red-500/30",
    icon: XCircle,
    description: "Declined/rejected",
    actionable: false,
    canTransitionTo: ["pending"], // Allow resubmission
  },
  on_hold: {
    label: "On Hold",
    color: "text-gray-400",
    bgColor: "bg-gray-500/20",
    borderColor: "border-gray-500/30",
    icon: Pause,
    description: "Temporarily paused",
    actionable: true,
    canTransitionTo: ["in_review", "approved", "rejected"],
  },
  revision_pending: {
    label: "Revision Pending",
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/20",
    borderColor: "border-cyan-500/30",
    icon: RotateCcw,
    description: "Waiting for affiliate to submit revisions",
    actionable: true,
    canTransitionTo: ["in_review", "changes_needed", "rejected"],
  },
};

// Priority levels
export const PRIORITY_LEVELS = {
  low: {
    label: "Low",
    color: "text-gray-400",
    bgColor: "bg-gray-500/20",
    borderColor: "border-gray-500/30",
  },
  normal: {
    label: "Normal",
    color: "text-blue-400",
    bgColor: "bg-blue-500/20",
    borderColor: "border-blue-500/30",
  },
  high: {
    label: "High",
    color: "text-orange-400",
    bgColor: "bg-orange-500/20",
    borderColor: "border-orange-500/30",
  },
  urgent: {
    label: "Urgent",
    color: "text-red-400",
    bgColor: "bg-red-500/20",
    borderColor: "border-red-500/30",
  },
};

// Get status configuration
export const getStatusConfig = (status) => {
  return SUBMISSION_STATUSES[status] || SUBMISSION_STATUSES.pending;
};

// Get priority configuration
export const getPriorityConfig = (priority) => {
  return PRIORITY_LEVELS[priority] || PRIORITY_LEVELS.normal;
};

// Get available status transitions for a given status
export const getAvailableTransitions = (currentStatus) => {
  const config = getStatusConfig(currentStatus);
  return config.canTransitionTo || [];
};

// Check if a status transition is valid
export const isValidTransition = (fromStatus, toStatus) => {
  const availableTransitions = getAvailableTransitions(fromStatus);
  return availableTransitions.includes(toStatus);
};

// Get status badge component props
export const getStatusBadgeProps = (status) => {
  const config = getStatusConfig(status);
  return {
    className: `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color} ${config.bgColor} border ${config.borderColor}`,
    icon: config.icon,
    label: config.label,
    description: config.description,
  };
};

// Get priority badge component props
export const getPriorityBadgeProps = (priority) => {
  const config = getPriorityConfig(priority);
  return {
    className: `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color} ${config.bgColor} border ${config.borderColor}`,
    label: config.label,
  };
};

// Status workflow helpers
export const getStatusWorkflowStage = (status) => {
  const stages = {
    pending: "submission",
    uploading: "submission",
    in_review: "review",
    changes_needed: "revision",
    revision_pending: "revision",
    approved: "approved",
    launched: "completed",
    rejected: "completed",
    on_hold: "paused",
  };
  return stages[status] || "unknown";
};

// Get next logical status suggestions
export const getNextStatusSuggestions = (currentStatus) => {
  const suggestions = {
    pending: ["in_review"],
    uploading: ["in_review"],
    in_review: ["approved", "changes_needed"],
    changes_needed: ["revision_pending"],
    revision_pending: ["in_review"],
    approved: ["launched"],
    launched: [],
    rejected: [],
    on_hold: ["in_review"],
  };
  return suggestions[currentStatus] || [];
};

// Format status history for display
export const formatStatusHistory = (statusHistory) => {
  if (!statusHistory || !Array.isArray(statusHistory)) return [];

  return statusHistory.map((entry) => ({
    ...entry,
    fromStatusConfig: entry.from_status
      ? getStatusConfig(entry.from_status)
      : null,
    toStatusConfig: getStatusConfig(entry.to_status),
    formattedDate: new Date(entry.changed_at).toLocaleString(),
  }));
};
